/**
 * "From the mail to the draft" (docs section 4): one run of the mail-ingest job as a
 * Timeline of its six main stages in the order of `run_ingest`, and the thirteen steps that
 * one mail passes inside the stage PROCESS. Every station names its module under
 * `backend/src/rfq_foundry/`. A stage with the switch badge runs only while its switch row
 * is on. A step with the environment switch badge runs only for the entities that its
 * variable names (13 Oct 2026: the verifier, the second opinion, the investigator with its
 * refute step, pre-flight; each default is off). On a laptop the steps stand beside the
 * run, with an arrow from PROCESS; on a phone they stand under it (the narrow media
 * query). Six stations in one row are too narrow to read in the documentation column, so
 * both Timelines are vertical.
 */

import {
  ArrowDown,
  ArrowRight,
  BadgeCheck,
  Cog,
  Icon,
  type IconComponent,
  MailPlus,
  PenLine,
  Send,
  ToggleRight,
  Trash2,
} from "@norican/icons";
import { Badge, Card, cn, Timeline, type TimelineItem, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { NARROW_QUERY, useMediaQuery } from "@/hooks/use-media-query";

import { Figure } from "./figure";

export const MAIL_TO_DRAFT_PIPELINE_TITLE = "From the mail to the draft";

/** What the two parts of the drawing show, for a screen reader. */
export const RUN_SUMMARY = "The six main stages of one run of the mail-ingest job, in order";
export const STEPS_SUMMARY = "The thirteen steps for one mail inside the stage PROCESS, in order";

/** A step: its name, its module, one short line (backticks mark code names). */
type Step = readonly [title: string, path: string, text: string];

/** The six main stages of `run_ingest`, by the names the code and the job log use. */
export const RUN_STAGES: readonly Step[] = [
  ["FETCH", "mail/ingest.py", "The Graph delta gives the new mail of each mailbox."],
  ["PROCESS", "mail/ingest.py", "One mail becomes a case with a stored draft."],
  ["OUTCOMES", "mail/outcomes.py", "The reply that sales people sent, from Sent Items."],
  ["GROUND TRUTH", "mail/ground_truth.py", "The quotation in F&O, from the mirror."],
  ["PREPARE_DRAFTS", "drafting/prepare.py", "The reply text for a ready inquiry."],
  ["PURGE", "mail/raw_retention.py", "It removes old raw mail. `KEEP` runs first."],
];
/** The icon of each stage, in the order of the stages. */
const STAGE_ICONS: readonly IconComponent[] = [MailPlus, Cog, Send, BadgeCheck, PenLine, Trash2];
/** The switch row that gates a stage: the stage runs only while the row is on. */
export const STAGE_GATES: Readonly<Record<string, string>> = {
  "GROUND TRUTH": "oa_ground_truth",
  PREPARE_DRAFTS: "autonomy_prefill_reply_draft",
  PURGE: "raw_retention",
};

/** What `process_case` and `quote_enquiry` do with one mail, in order. */
export const PROCESS_STEPS: readonly Step[] = [
  ["Mail facts", "mail/mailfacts.py", "A second copy of a mail stops here, with no model call."],
  ["Intent", "engine/pipeline/intent.py", "One model call. Only an inquiry goes on."],
  ["Documents", "mail/attach_lines.py", "PDF, Excel, scans and photos give lines."],
  ["Customer", "engine/pipeline/quote_agent.py", "Who asks: certain, suggested or open."],
  ["Lines", "engine/pipeline/field_capture.py", "The mail text gives lines too. One list goes on."],
  ["Items", "engine/pipeline/resolve.py", "The matching ladder finds one item for each line."],
  ["Sources for spare parts", "spare/lines.py", "Five sources add or change lines."],
  [
    "Verifier",
    "spare/verifier.py",
    "One model call checks the proposed items. It keeps or removes an automatic accept.",
  ],
  [
    "Second opinion",
    "calibrate/live.py",
    "A calibrated score for each line with an item. In the mode `gate` it removes an accept.",
  ],
  [
    "Investigator and refute step",
    "matching/investigation/coordinator.py",
    // Round 2 (13 Oct 2026): the playbooks, the revision pass and the second pass run
    // inside this step, each behind its own switch (docs section 6).
    "A loop with read-only tools works on an open case. Playbooks make the first reads. A second call tries to refute each proposal. A second pass reads provisional values.",
  ],
  [
    "Pre-flight",
    "preflight/live.py",
    "It checks the finished draft against the master data. The rule flags show the findings.",
  ],
  ["Prices and rule flags", "rules/evaluate.py", "Entity rules read prices and flag the draft."],
  ["The stored draft", "store/cases.py", "One upsert writes `rfq_case` and `rfq_case_draft`."],
];
/**
 * The environment switch that gates a step: the step runs only for the entities that the
 * variable names. Each default is off (docs section 6).
 */
export const STEP_GATES: Readonly<Record<string, string>> = {
  Verifier: "RFQ_SPARE_VERIFIER",
  "Second opinion": "RFQ_CALIBRATION_MODE",
  "Investigator and refute step": "RFQ_MATCHING_INVESTIGATION_MODE",
  "Pre-flight": "RFQ_PREFLIGHT",
};

export const RUN_HEADING = "One run of the mail-ingest job";
export const STEPS_HEADING = "Inside `PROCESS`: the steps for one mail";
export const ARROW_WORD = "one mail";
export const SWITCH_BADGE = "switch row";
export const SWITCH_NOTE = "A stage with this badge runs only while the named switch row is ON.";
export const ENV_BADGE = "environment switch";
export const ENV_NOTE =
  "A step with this badge runs only for the entities that its variable names. Each default is off. By default, no such step accepts a line or makes a customer certain.";
export const PATHS_NOTE =
  "Every path is under `backend/src/rfq_foundry/`. A run has more stages than these six. `REDERIVE` runs before `FETCH`. `MEMORY`, `LINKS`, `DESK STATE`, `AUTOCLOSE` and `KEEP` run between `OUTCOMES` and `GROUND TRUTH`.";

const COLUMN = "flex min-w-0 flex-col gap-3";
const SMALL_CODE = "text-[0.8em] break-all";

/** Backtick spans in code font, as the documentation page prints them. */
function code(text: string): ReactNode {
  return text.split("`").map((part, index) =>
    index % 2 ? (
      <Typography key={index} as="code" variant="code" className="text-[0.9em] break-words">
        {part}
      </Typography>
    ) : (
      part
    ),
  );
}

/** The badge of a gated station: a switch row (a stage) or an environment switch (a step). */
function SwitchBadge({ word = SWITCH_BADGE }: { word?: string }) {
  return (
    <Badge tone="warning" variant="outline" size="sm">
      <Icon icon={ToggleRight} size="xs" />
      {word}
    </Badge>
  );
}

/** A station as a Timeline item: the marker, the name, the line, the module, the switch. */
function item(
  [title, path, text]: Step,
  marker: ReactNode,
  gate?: string,
  badge: string = SWITCH_BADGE,
): TimelineItem {
  return {
    id: title,
    title: <span className="wrap-anywhere">{title}</span>,
    tone: title === "PROCESS" ? "accent" : "default",
    icon: marker,
    description: (
      <span className="flex min-w-0 flex-col items-start gap-1">
        <span className="break-words">{code(text)}</span>
        <Typography as="code" variant="code" tone="muted" className={SMALL_CODE}>
          {path}
        </Typography>
        {gate && (
          <span className="flex min-w-0 flex-wrap items-center gap-1.5" data-gate={gate}>
            <SwitchBadge word={badge} />
            <Typography as="code" variant="code" className={SMALL_CODE}>
              {gate}
            </Typography>
          </span>
        )}
      </span>
    ),
  };
}

/** `layout` overrides the media query for the render tests. Omit it in the app. */
export function MailToDraftPipelineFigure(props: {
  caption?: string;
  layout?: "beside" | "stacked";
}) {
  const narrow = useMediaQuery(NARROW_QUERY);
  const form = props.layout ?? (narrow ? "stacked" : "beside");
  const stacked = form === "stacked";
  const stages = RUN_STAGES.map((stage, index) =>
    item(stage, <Icon icon={STAGE_ICONS[index] ?? Cog} size="sm" />, STAGE_GATES[stage[0]]),
  );
  const steps = PROCESS_STEPS.map((step, index) =>
    item(
      step,
      <Badge tone="accent" variant="subtle" size="sm" className="tabular-nums">
        {index + 1}
      </Badge>,
      STEP_GATES[step[0]],
      ENV_BADGE,
    ),
  );
  return (
    <Figure
      name="mail-to-draft-pipeline"
      title={MAIL_TO_DRAFT_PIPELINE_TITLE}
      caption={props.caption}
    >
      <div
        data-layout={form}
        className={stacked ? COLUMN : "grid grid-cols-[minmax(0,5fr)_auto_minmax(0,7fr)] gap-3"}
      >
        <div className={COLUMN}>
          <Typography variant="label">{code(RUN_HEADING)}</Typography>
          <Timeline
            orientation="vertical"
            connector="arrow"
            items={stages}
            aria-label={RUN_SUMMARY}
          />
          <span className="text-fg-muted flex flex-wrap items-center gap-1.5 text-xs">
            <SwitchBadge />
            {SWITCH_NOTE}
          </span>
        </div>
        {/* The arrow starts beside the stage PROCESS, the second station of the run. */}
        <span
          className={cn("text-fg-muted flex flex-col items-center text-xs", !stacked && "pt-28")}
        >
          {!stacked && ARROW_WORD}
          <Icon icon={stacked ? ArrowDown : ArrowRight} size="sm" label="to" />
        </span>
        <Card
          variant="outline"
          padding="sm"
          accent="left"
          accentTone="accent"
          role="group"
          aria-label={STEPS_SUMMARY}
          className={COLUMN}
        >
          <Typography variant="label">{code(STEPS_HEADING)}</Typography>
          <Timeline orientation="vertical" connector="arrow" items={steps} />
          <span className="text-fg-muted flex flex-wrap items-center gap-1.5 text-xs">
            <SwitchBadge word={ENV_BADGE} />
            {ENV_NOTE}
          </span>
        </Card>
      </div>
      <Typography variant="caption" tone="muted" className="break-words">
        {code(PATHS_NOTE)}
      </Typography>
    </Figure>
  );
}
